import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from 'src/shared/components/ui/sheet';
import { Input } from 'src/shared/components/ui/input';
import { Button } from 'src/shared/components/ui/button';
import { isEmailIdValid } from 'src/shared/helpers/emailHelpers';
import { GraphApiError } from 'src/shared/state/api-slices/graphApiSlice';
import { useCreateProjectMutation } from 'src/app/admin/state/api/adminGraphSlice';

type AddProjectSheetProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    customerId: string;
    customerName: string;
};

const DUPLICATE_PROJECT_ID_CODE = 'DUPLICATE_PROJECT_ID';

const getErrorMessage = (error: unknown): string => {
    // The backend reports this by code on some paths and by message alone on others — both read
    // off the same failure, so a code from one error is never paired with a message from another.
    const clashed = (error as GraphApiError)?.failures?.some(
        failure => failure.code === DUPLICATE_PROJECT_ID_CODE || /already exists/i.test(failure.message ?? '')
    );

    if (clashed) {
        return 'A project with that Project ID already exists.';
    }
    return 'Something went wrong creating the project. Please try again.';
};

const AddProjectSheet = ({ open, onOpenChange, customerId, customerName }: AddProjectSheetProps) => {
    const [projectId, setProjectId] = useState('');
    const [projectName, setProjectName] = useState('');
    const [leadName, setLeadName] = useState('');
    const [leadEmail, setLeadEmail] = useState('');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [createProject, { isLoading }] = useCreateProjectMutation();

    const trimmedEmail = leadEmail.trim();
    const isEmailInvalid = trimmedEmail !== '' && !isEmailIdValid(trimmedEmail);
    const canSubmit = projectId.trim() !== '' && projectName.trim() !== '' && !isEmailInvalid && !isLoading;

    const resetForm = () => {
        setProjectId('');
        setProjectName('');
        setLeadName('');
        setLeadEmail('');
        setErrorMessage(null);
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            resetForm();
        }
        onOpenChange(nextOpen);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!canSubmit) {
            return;
        }
        setErrorMessage(null);
        try {
            await createProject({
                customerId,
                projectId,
                projectName,
                // An untouched lead field means "no lead", not "a lead with an empty name".
                ...(leadName.trim() ? { projectLeadName: leadName } : {}),
                ...(trimmedEmail ? { projectLeadEmail: leadEmail } : {}),
            }).unwrap();
            resetForm();
            onOpenChange(false);
        } catch (error) {
            setErrorMessage(getErrorMessage(error));
        }
    };

    return (
        <Sheet open={open} onOpenChange={handleOpenChange}>
            <SheetContent>
                <SheetHeader>
                    <SheetTitle>Add project</SheetTitle>
                    <SheetDescription>
                        Create a project under {customerName}. Its survey definition is added separately, once the project exists.
                    </SheetDescription>
                </SheetHeader>

                <form onSubmit={handleSubmit} className='flex flex-col gap-4 mt-6' data-testid='add-project-form'>
                    <div className='flex flex-col gap-1'>
                        <label htmlFor='add-project-id' className='text-sm font-medium'>
                            Project ID
                        </label>
                        <Input
                            id='add-project-id'
                            value={projectId}
                            onChange={event => setProjectId(event.target.value)}
                            placeholder='e.g. P00123'
                        />
                    </div>

                    <div className='flex flex-col gap-1'>
                        <label htmlFor='add-project-name' className='text-sm font-medium'>
                            Project name
                        </label>
                        <Input
                            id='add-project-name'
                            value={projectName}
                            onChange={event => setProjectName(event.target.value)}
                            placeholder='Project name'
                        />
                    </div>

                    <div className='flex flex-col gap-1'>
                        <label htmlFor='add-project-lead-name' className='text-sm font-medium'>
                            Project lead name <span className='text-grey-70 font-normal'>(optional)</span>
                        </label>
                        <Input id='add-project-lead-name' value={leadName} onChange={event => setLeadName(event.target.value)} />
                    </div>

                    <div className='flex flex-col gap-1'>
                        <label htmlFor='add-project-lead-email' className='text-sm font-medium'>
                            Project lead email <span className='text-grey-70 font-normal'>(optional)</span>
                        </label>
                        <Input
                            id='add-project-lead-email'
                            type='email'
                            value={leadEmail}
                            onChange={event => setLeadEmail(event.target.value)}
                        />
                    </div>

                    {(isEmailInvalid || errorMessage) && (
                        <p role='alert' className='text-sm text-red-100'>
                            {isEmailInvalid ? 'Enter a valid email address for the project lead.' : errorMessage}
                        </p>
                    )}

                    <SheetFooter className='mt-2'>
                        <Button type='button' variant='outline' onClick={() => handleOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type='submit' disabled={!canSubmit} className='flex-grow'>
                            Add project
                        </Button>
                    </SheetFooter>
                </form>
            </SheetContent>
        </Sheet>
    );
};

export default AddProjectSheet;
